// The reconciliation report, as a PDF for the record.
//
// WHAT GOES IN, AND WHAT DELIBERATELY DOES NOT
// ============================================
// Everything needed to defend the reconciliation later: the scope, the figures
// on both sides, what was corrected and by whom, and anything that limits how
// far the result can be trusted.
//
// Entries that AGREED with the register are reported as a count and a total, not
// listed one by one — a hundred-line table saying "fine" is noise, and nobody
// reads it. Entries found INCORRECT are listed in full, because those are the
// ones somebody has to act on and the ones a reader will question.
//
// The outcome is stamped at the top whatever it is. An unreconciled period still
// produces a report, clearly marked — a record that a reconciliation was
// attempted and did not close is worth more than no record at all.

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';

import { ASSETPRO_LOGO_WHITE_PNG, LOGO_ASPECT } from './brandAssets';
import { SITE_TYPE_LABEL, VERDICT_LABEL } from './reconEngine';
import { fromCl } from './registerNormalize';

// AssetPro Design Foundations — the light-surface tokens, since a printed page is
// a light surface. Taken from the brand sheet rather than picked by eye:
//   --brand-navy   #0A1220   headers and rules
//   --brand-orange #FF6B19   the brand accent
//   --azure-500    #1E88FA   information
//   --success-500  #2FD27A / --success-600 #1FA862
//   --text-body    #223448
//   --surface-raised #F1F4F9, --border-subtle rgba(15,40,70,.10)
const NAVY = [10, 18, 32];
const ORANGE = [255, 107, 25];
const GREEN = [31, 168, 98];
const RED = [226, 59, 65];      // --critical-600, the light-surface step
const AMBER = [217, 138, 0];    // --warning-600
const TEXT_BODY = [34, 52, 72];
const TEXT_MUTED = [110, 134, 159];
const SURFACE_RAISED = [241, 244, 249];

const L = (cl) => (cl === null || cl === undefined ? null : fromCl(cl));
const n2 = (v) => (v === null || v === undefined || !Number.isFinite(Number(v))
  ? '-'
  : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export const buildReconciliationPdf = ({
  recon,
  summary,
  worklist,
  dayWiseByTarget = {},
  periodFrom,
  periodTo,
  filters = {},
  siteTypeLabel,
}) => {
  // Landscape: the incorrect-entries table carries eight columns, and portrait
  // wraps dates and headings mid-word.
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 12;
  let y = 0;

  const room = (needed) => {
    if (y + needed > pageH - M - 8) { doc.addPage(); y = M; }
  };
  const section = (title, color = NAVY) => {
    room(18);
    doc.setFillColor(...color);
    doc.rect(M, y, pageW - M * 2, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(title, M + 3, y + 4.9);
    y += 10;
  };
  const para = (text, color = TEXT_MUTED, size = 8.5) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, pageW - M * 2);
    room(lines.length * 4 + 3);
    doc.text(lines, M, y);
    y += lines.length * 4 + 3;
  };
  const table = (head, body, opts = {}) => {
    autoTable(doc, {
      startY: y,
      head: [head],
      body,
      styles: { fontSize: 7.8, cellPadding: 1.8, overflow: 'linebreak', textColor: TEXT_BODY },
      alternateRowStyles: { fillColor: SURFACE_RAISED },
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.8, halign: 'left' },
      margin: { left: M, right: M },
      ...opts,
    });
    y = doc.lastAutoTable.finalY + 6;
  };

  // --- title bar -----------------------------------------------------------
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 26, 'F');
  // The brand accent rule under the header, per the design foundations.
  doc.setFillColor(...ORANGE);
  doc.rect(0, 26, pageW, 1.2, 'F');

  // The wordmark sits top-right with its own clear space, so the title reads first.
  const logoW = 38;
  const logoH = logoW / LOGO_ASPECT;
  try {
    doc.addImage(ASSETPRO_LOGO_WHITE_PNG, 'PNG', pageW - M - logoW, 7, logoW, logoH);
  } catch (e) {
    // A missing logo must never cost someone their report.
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('AssetPro', pageW - M, 13, { align: 'right' });
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text('Fuel Register Reconciliation', M, 11);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const scope = [
    `${moment(periodFrom).format('DD MMM YYYY')} - ${moment(periodTo).format('DD MMM YYYY')}`,
    filters.fFuelType || 'All fuel types',
    (filters.fLocation || []).length ? filters.fLocation.join(', ') : 'All locations',
    (filters.fSubLocation || []).length ? filters.fSubLocation.join(', ') : null,
  ].filter(Boolean).join('   |   ');
  doc.text(scope, M, 17);
  doc.text(`${siteTypeLabel || SITE_TYPE_LABEL[recon.siteType]}   |   Generated ${moment().format('DD MMM YYYY HH:mm')}`, M, 21.5);
  y = 34;

  // --- outcome -------------------------------------------------------------
  const reconciled = recon.state === 'RECONCILED';
  const banner = reconciled ? GREEN : recon.state === 'DIFFERS' ? RED : AMBER;
  const headline = reconciled
    ? (recon.reconciledByCorrections ? 'RECONCILED - after the corrections recorded below' : 'RECONCILED')
    : recon.state === 'DIFFERS'
      ? 'NOT RECONCILED - a difference remains'
      : recon.state === 'INCOMPLETE'
        ? 'INCOMPLETE - not every required figure was entered'
        : 'NOT STARTED - no register figures were entered';
  doc.setFillColor(...banner);
  doc.rect(M, y, pageW - M * 2, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(headline, M + 3, y + 6.8);
  y += 15;

  if (recon.reconciledByCorrections) {
    para('The figures did not agree as they stood. They agree with the corrections listed in this report applied. Those corrections still have to be actioned in AssetPro - this report records what was found, it does not change anything in the source system.', AMBER);
  }

  // --- the figures ---------------------------------------------------------
  section('The reconciliation');
  table(
    ['What is being reconciled', 'Per the register', 'Per AssetPro', 'After corrections', 'Difference', 'Result'],
    recon.targets.map((t) => [
      t.label + (t.optional ? '  (optional)' : ''),
      t.regCl === null ? 'not entered' : n2(L(t.regCl)),
      n2(L(t.apCl)),
      t.hasCorrections ? n2(L(t.adjustedApCl)) : '-',
      t.adjustedDiffCl === null ? '-' : n2(L(t.adjustedDiffCl)),
      t.reconciled === null ? '-' : t.reconciled ? 'matches' : 'OUT',
    ]),
    {
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'right', cellWidth: 32 },
        2: { halign: 'right', cellWidth: 32 },
        3: { halign: 'right', cellWidth: 34 },
        4: { halign: 'right', cellWidth: 28 },
        5: { halign: 'center', cellWidth: 22 },
      },
      didParseCell: (d) => {
        if (d.section === 'body' && d.column.index === 5) {
          if (d.cell.raw === 'matches') d.cell.styles.textColor = GREEN;
          if (d.cell.raw === 'OUT') { d.cell.styles.textColor = RED; d.cell.styles.fontStyle = 'bold'; }
        }
      },
    }
  );
  para('"Per AssetPro" is AssetPro\'s own figure. "After corrections" is that figure with the corrections in this report applied - it is shown only where a correction affects it, and is never written back to AssetPro.');

  // --- anything limiting the result ---------------------------------------
  if (recon.dataIssues && recon.dataIssues.length) {
    section("AssetPro's own figures disagree with each other", RED);
    recon.dataIssues.forEach((d) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...TEXT_BODY);
      room(10);
      doc.text(d.title, M, y);
      y += 4;
      para(d.detail);
    });
  }

  // --- day-wise, only where it was used ------------------------------------
  Object.keys(dayWiseByTarget).forEach((key) => {
    const dw = dayWiseByTarget[key];
    if (!dw || !dw.entered) return;
    const target = recon.targets.find((t) => t.key === key);
    section(`Day-wise - ${target ? target.label : key}`);
    const broke = dw.breakDays || [];
    // A balance is anchored on the last day entered; a daily flow is not, so the
    // report must not print an "Expected / Anchored on" pair that means nothing.
    const isBalance = dw.measure !== 'FLOW';
    para(broke.length
      ? `A difference was created on ${broke.length} day(s): ${broke.join(', ')}. ${isBalance
        ? 'Each day is measured from the last day entered, so these are the days the two sides actually moved apart rather than days carrying an earlier gap.'
        : 'Each day stands on its own, so these are the days the two totals differ.'}`
      : 'Every day filled in agreed with AssetPro.');
    table(
      isBalance
        ? ['Date', 'Per the register', 'Per AssetPro', 'Expected', 'Anchored on', 'Difference']
        : ['Date', 'Per the register', 'Per AssetPro', 'After corrections', 'Difference'],
      dw.rows.filter((r) => r.entered).map((r) => (isBalance
        ? [
          r.date,
          n2(L(r.regCl)),
          n2(L(r.apCl)),
          n2(L(r.expectedCl)),
          r.anchorDate || 'AssetPro',
          r.adjustedDiffCl === null ? '-' : n2(L(r.adjustedDiffCl)),
        ]
        : [
          r.date,
          n2(L(r.regCl)),
          n2(L(r.apCl)),
          n2(L(r.adjustedApCl)),
          r.adjustedDiffCl === null ? '-' : n2(L(r.adjustedDiffCl)),
        ])),
      {
        columnStyles: isBalance
          ? {
            0: { cellWidth: 26 },
            1: { halign: 'right', cellWidth: 32 },
            2: { halign: 'right', cellWidth: 32 },
            3: { halign: 'right', cellWidth: 32 },
            4: { cellWidth: 'auto' },
            5: { halign: 'right', cellWidth: 30 },
          }
          : {
            0: { cellWidth: 26 },
            1: { halign: 'right', cellWidth: 36 },
            2: { halign: 'right', cellWidth: 36 },
            3: { halign: 'right', cellWidth: 'auto' },
            4: { halign: 'right', cellWidth: 32 },
          },
      }
    );
    if (dw.gaps) para(`${dw.gaps} day(s) in the period were left blank, so a difference could still be hiding in those.`, AMBER);
  });

  // --- entries -------------------------------------------------------------
  section('Entries checked against the register');
  const c = summary.counts;
  if (c.total === 0) {
    para('There were no AssetPro entries in this scope.');
  } else if (c.reviewed === 0 && reconciled) {
    para(`The figures agreed, so the ${c.total} entries behind them are taken to be correct and were not checked individually.`);
  } else if (c.reviewed === 0) {
    // Saying "the figures agreed" on a report stamped NOT RECONCILED would
    // contradict its own headline.
    para(`None of the ${c.total} entries in scope were checked against the register. The difference above is therefore unattributed - working through the entries is what locates it.`, AMBER);
  } else {
    const okL = summary.reviewed
      .filter((r) => r.verdict === 'OK')
      .reduce((a, r) => a + (Number(r.entry.quantity) || 0), 0);
    table(
      ['Outcome', 'Entries', 'Litres'],
      [
        ['Agreed with the register', String(c.ok), n2(okL)],
        ['Found incorrect', String(c.flagged), '-'],
        ['Not checked', String(c.unreviewed), n2(summary.unreviewedL)],
        ['Total in scope', String(c.total), '-'],
      ],
      { columnStyles: { 0: { cellWidth: 'auto' }, 1: { halign: 'right', cellWidth: 28 }, 2: { halign: 'right', cellWidth: 30 } } }
    );
    para('Entries that agreed are reported as a total only - they need no action, and listing them one by one would bury the ones that do.');
    if (c.unreviewed > 0) {
      para(`${c.unreviewed} entries covering ${n2(summary.unreviewedL)} L were not checked. Any difference could still sit inside them.`, AMBER);
    }
  }

  // --- the incorrect ones, in full -----------------------------------------
  if (worklist.length) {
    section(`Entries found incorrect (${worklist.length})`, RED);
    para('These have to be corrected in AssetPro. Nothing in this report has changed the source system. "Effect on balance" is how much the tank\'s closing balance moves once a correction is made; a blank means it does not touch the tank - the entry is still wrong and still needs fixing.');
    table(
      ['Date', 'Type', 'Machine', 'AssetPro', 'Finding', 'What to do', 'Effect on balance', 'Checked by'],
      worklist.map((w) => [
        w.date || '-',
        w.kind || '-',
        w.assetCode || '-',
        w.currentQtyL === null || w.currentQtyL === undefined ? '-' : n2(w.currentQtyL),
        w.verdictLabel || VERDICT_LABEL[w.verdict] || w.verdict,
        [w.action, w.note ? `Note: ${w.note}` : null].filter(Boolean).join('\n'),
        w.impactL === null || w.impactL === undefined || w.impactL === 0 ? '-' : n2(w.impactL),
        w.reviewedBy || '-',
      ]),
      {
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 20 },
          2: { cellWidth: 24 },
          3: { halign: 'right', cellWidth: 18 },
          4: { cellWidth: 30 },
          5: { cellWidth: 'auto' },
          6: { halign: 'right', cellWidth: 20 },
          7: { cellWidth: 24 },
        },
      }
    );
  } else if (summary.counts.reviewed > 0) {
    section('Entries found incorrect', GREEN);
    para('None. Every entry checked agreed with the register.');
  }

  // --- footer --------------------------------------------------------------
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...TEXT_MUTED);
    doc.setDrawColor(220, 226, 236);
    doc.line(M, pageH - 9.5, pageW - M, pageH - 9.5);
    doc.text(
      'AssetPro - record of a fuel register reconciliation. Figures shown as "-" are missing or not applicable, never zero.',
      M, pageH - 6
    );
    doc.text(`Page ${i} of ${pages}`, pageW - M, pageH - 6, { align: 'right' });
  }

  return doc;
};

export const downloadReconciliationPdf = (args) => {
  const doc = buildReconciliationPdf(args);
  doc.save(`fuel-reconciliation-${args.periodFrom}_${args.periodTo}.pdf`);
};

export default downloadReconciliationPdf;
